import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Share2, Info, FileText, Mail, LogOut, AlertCircle, Gift, Copy, Shield, HelpCircle, MessageSquare, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { resetOnboarding } from '../components/Onboarding';

export default function SharePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const shareApp = async () => {
    const referralToken = user?.referral_token || '';
    const baseText = 'Check out LexProcure PH - Your complete digital reference for Philippine procurement laws (RA 9184, RA 12009 & IRRs)!';
    const url = window.location.origin;
    
    // Include referral token if available
    const text = referralToken 
      ? `${baseText}\n\nUse my referral token when you sign up: ${referralToken}`
      : baseText;
    
    const shareContent = `${text}\n\n${url}`;
    
    try {
      await navigator.clipboard.writeText(shareContent);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Failed to copy. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Share & More
          </h1>
          <p className="text-slate-600 dark:text-slate-400">Share the app, earn rewards, and access helpful information</p>
        </div>

        {/* Share App */}
        <section className="mb-8">
          <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
                    Share <span style={{ color: '#FFD700' }}>Lex</span>Procure PH
                  </h3>
                  <p className="text-emerald-100">
                    Help others discover this comprehensive procurement law library
                  </p>
                </div>
                <Button
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-slate-100 font-semibold"
                  onClick={shareApp}
                  data-testid="share-app-btn"
                >
                  <Share2 className="mr-2 w-5 h-5" /> Share App
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Unique Token Code */}
        <section className="mb-12">
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-white">
                <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Your Referral Token
              </CardTitle>
              <CardDescription className="dark:text-slate-400">
                Share this token with colleagues. When they subscribe, you earn rewards!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Token Display */}
              <div className="bg-gradient-to-r from-purple-50 to-emerald-50 dark:from-purple-900/20 dark:to-emerald-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-lg p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-xs text-purple-700 dark:text-purple-300 mb-2 font-semibold">Your Token Code</p>
                    <code className="text-2xl font-bold font-mono text-purple-600 dark:text-purple-300 tracking-wider">
                      {user?.referral_token || 'Loading...'}
                    </code>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (user?.referral_token) {
                        navigator.clipboard.writeText(user.referral_token);
                        toast.success('Token code copied to clipboard!');
                      }
                    }}
                    className="border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
                
                {/* Auto-included notice */}
                <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-700">
                  <p className="text-xs text-purple-700 dark:text-purple-300 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    <span><strong>Note:</strong> This token is automatically included when you click "Share App" above</span>
                  </p>
                </div>
              </div>

              {/* How It Works */}
              <div className="bg-white dark:bg-slate-700 border border-emerald-200 dark:border-emerald-700 rounded-lg p-5">
                <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-3 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  How Referrals Work
                </p>
                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-purple-600 dark:text-purple-400">1.</span>
                    <p>Share your token with colleagues via email, SMS, or the "Share App" button</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-600 dark:text-blue-400">2.</span>
                    <p>They enter your token when signing up for LexProcure PH</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">3.</span>
                    <p>When they become paid subscribers, you become eligible for rewards!</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Information Links */}
        <section>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6" style={{ fontFamily: 'IBM Plex Sans' }}>
            Information
          </h2>
          <div className="space-y-3">
            <Card className="hover:shadow-md transition-all cursor-pointer dark:bg-slate-800 dark:border-slate-700" data-testid="about-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <Info className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  About Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  <strong>LexProcure PH</strong> is a proprietary application owned by <strong>SRAGCircle OPC</strong>, developed under its portfolio brand <strong>LexDoRight</strong>.
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  It is a comprehensive digital library designed to make Philippine procurement laws accessible to students, professionals, and practitioners.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all dark:bg-slate-800 dark:border-slate-700" data-testid="onboarding-support-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <HelpCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  Onboarding Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  Get help and learn about app features
                </p>
                <Button
                  variant="outline"
                  className="w-full justify-start hover:bg-emerald-50 dark:hover:bg-slate-700"
                  onClick={() => {
                    resetOnboarding();
                    toast.success('Tutorial reset! Visit the Library page to see it again.');
                  }}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Retake Tutorial
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all dark:bg-slate-800 dark:border-slate-700" data-testid="legal-policies-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  Legal & Policies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-left hover:bg-emerald-50 dark:hover:bg-slate-700"
                  onClick={() => navigate('/terms')}
                >
                  Terms & Conditions
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-left hover:bg-emerald-50 dark:hover:bg-slate-700"
                  onClick={() => navigate('/privacy')}
                >
                  Privacy Policy
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-left hover:bg-emerald-50 dark:hover:bg-slate-700"
                  onClick={() => navigate('/refund-policy')}
                >
                  Refund Policy
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all dark:bg-slate-800 dark:border-slate-700" data-testid="contact-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <Mail className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    Have questions or feedback? We'd love to hear from you, so email us at:
                  </p>
                  <a
                    href="mailto:support@lexdoright.com?subject=Query/Comments about LexProcure PH"
                    className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium text-sm block"
                  >
                    support@lexdoright.com
                  </a>
                  <p className="text-xs text-slate-500 dark:text-slate-500 italic mt-1">
                    Subject: "Query/Comments about LexProcure PH"
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    For technical support, please email:
                  </p>
                  <a
                    href="mailto:support@lexdoright.com?subject=Request for LexProcure PH Support"
                    className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium text-sm block"
                  >
                    support@lexdoright.com
                  </a>
                  <p className="text-xs text-slate-500 dark:text-slate-500 italic mt-1">
                    Subject: "Request for LexProcure PH Support"
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-all dark:bg-slate-800 dark:border-slate-700" data-testid="feedback-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  Feedback & Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Want to share your feedback, inputs, or suggestions for improving the LexProcure PH app? Join our community!
                </p>
                <div className="bg-white dark:bg-slate-700 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
                  <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
                    Subscribe at the <strong>LexDoRight</strong> website and participate in our forum to:
                  </p>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 dark:text-emerald-400">•</span>
                      Share your ideas for new features
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 dark:text-emerald-400">•</span>
                      Report issues and suggest improvements
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 dark:text-emerald-400">•</span>
                      Connect with other procurement professionals
                    </li>
                  </ul>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => window.open('https://lexdoright.com', '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit LexDoRight Website
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white dark:bg-slate-800 dark:border-slate-700" data-testid="app-details-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg dark:text-slate-100">
                  <AlertCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  App Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">App Name:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">LexProcurePH</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Version:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">1.0.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Last Updated:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">January 2026</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>

      <BottomNav />
    </div>
  );
}
